import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { CreditCard, ExternalLink, Inbox, type LucideIcon, ServerCrash, ShoppingCart, Trash2 } from 'lucide-react'
import { apiFetch } from '../lib/api'
import type { Overview, UnpaidInvoices } from '../lib/types'
import { PageHeader, formatDate } from '../components/page'
import { EmptyState, ErrorState, Spinner } from '../components/states'
import { StatusBadge } from '../components/badge'

// One page that answers "how is CBB doing, and does anything need me?". The
// core numbers come from /api/admin/overview (our own database, fast). The
// cards that depend on Chargebee, Gmail or Rybbit each run their own query, so
// one slow or unconfigured service only blanks its own card.

const SYMBOLS: Record<string, string> = { GBP: '£', EUR: '€', USD: '$' }

function money(amount: number, currency: string | null): string {
	const formatted = amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
	if (!currency) return `${formatted} (no currency)`
	return SYMBOLS[currency] ? `${SYMBOLS[currency]}${formatted}` : `${formatted} ${currency}`
}

const num = (n: number) => n.toLocaleString()

export function DashboardPage() {
	const { data, isPending, error, refetch } = useQuery({
		queryKey: ['overview'],
		queryFn: () => apiFetch<Overview>('/api/admin/overview'),
	})

	return (
		<>
			<PageHeader title="Dashboard" subtitle="Customers, revenue and anything that needs attention." />

			{isPending ? (
				<Spinner />
			) : error ? (
				<div className="card">
					<ErrorState error={error} onRetry={refetch} />
				</div>
			) : (
				<div className="space-y-6">
					<Stats data={data} />

					<Section title="Needs attention">
						<div className="grid gap-4 md:grid-cols-2">
							<FailedInstances rows={data.attention.failedInstances} />
							<UpcomingDeletions rows={data.attention.upcomingDeletions} />
							<UnpaidCard />
							<SupportCard />
						</div>
					</Section>

					<Section title="Recent activity">
						<div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
							<ListCard
								title="Sign-ups"
								empty="No sign-ups yet."
								rows={data.recent.signups.map((u) => ({
									key: u.id,
									primary: u.email,
									secondary: formatDate(u.createdAt),
									to: `/users/${u.id}`,
								}))}
							/>
							<ListCard
								title="New subscriptions"
								empty="No new subscriptions recorded yet."
								rows={data.recent.subscriptions.map((e) => ({
									key: e.id,
									primary: e.summary,
									secondary: formatDate(e.createdAt),
								}))}
							/>
							<ListCard
								title="Cancellations"
								empty="No cancellations recorded yet."
								rows={data.recent.cancellations.map((e) => ({
									key: e.id,
									primary: e.summary,
									secondary: formatDate(e.createdAt),
								}))}
							/>
							<ListCard
								title="Activity log"
								empty="Nothing logged yet."
								footer={{ label: 'All logs', to: '/logs' }}
								rows={data.recent.activity.map((a) => ({
									key: a.id,
									primary: a.summary,
									secondary: `${a.actorEmail} · ${formatDate(a.createdAt)}`,
									badge: a.status === 'failure' ? 'failure' : undefined,
								}))}
							/>
						</div>
					</Section>

					<Section title="Trends">
						<div className="grid gap-4 lg:grid-cols-3">
							<div className="lg:col-span-2">
								<TrendChart trend={data.trend} />
							</div>
							<CheckoutCard />
						</div>
					</Section>
				</div>
			)}
		</>
	)
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
	return (
		<section>
			<h2 className="mb-3 font-display text-lg font-semibold">{title}</h2>
			{children}
		</section>
	)
}

// --- Top row -----------------------------------------------------------------

function Stats({ data }: { data: Overview }) {
	const { instances } = data
	const extra = [
		instances.pending ? `${num(instances.pending)} pending` : null,
		instances.cancelling ? `${num(instances.cancelling)} cancelling` : null,
		instances.failed ? `${num(instances.failed)} failed` : null,
	].filter(Boolean)

	return (
		<div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
			<StatCard
				label="Users"
				value={num(data.users.total)}
				detail={`+${num(data.users.newThisMonth)} signed up this month`}
			/>
			<StatCard
				label="Subscriptions"
				value={num(data.subscriptions.total)}
				detail={`from ${num(data.subscriptions.customers)} customers · ${num(data.subscriptions.monthly)} monthly, ${num(data.subscriptions.yearly)} yearly`}
			/>
			<StatCard
				label="Instances"
				value={num(instances.active ?? 0)}
				detail={extra.length ? extra.join(' · ') : 'all running'}
			/>
			<div className="card p-5">
				<p className="text-sm font-medium text-ink-soft">MRR</p>
				{data.mrr.length ? (
					<ul className="mt-2 space-y-0.5">
						{data.mrr.map((m) => (
							<li key={m.currency ?? 'none'} className="font-display text-2xl font-semibold tabular-nums">
								{money(m.amount, m.currency)}
							</li>
						))}
					</ul>
				) : (
					<p className="mt-2 font-display text-3xl font-semibold">—</p>
				)}
				<p className="mt-1 text-xs text-ink-soft">Per currency · yearly plans ÷ 12</p>
			</div>
		</div>
	)
}

function StatCard({ label, value, detail }: { label: string; value: string; detail: string }) {
	return (
		<div className="card p-5">
			<p className="text-sm font-medium text-ink-soft">{label}</p>
			<p className="mt-2 font-display text-3xl font-semibold tabular-nums">{value}</p>
			<p className="mt-1 text-xs text-ink-soft">{detail}</p>
		</div>
	)
}

// --- Needs attention ---------------------------------------------------------

function AttentionCard({
	icon: Icon,
	title,
	count,
	countLabel,
	children,
	footer,
}: {
	icon: LucideIcon
	title: string
	count?: number | string
	countLabel?: string
	children: React.ReactNode
	footer?: React.ReactNode
}) {
	const flagged = count !== undefined && count !== 0 && count !== '0'
	return (
		<div className="card flex flex-col">
			<div className="flex items-center justify-between gap-3 border-b border-line px-4 py-3">
				<div className="flex items-center gap-2">
					<Icon className={`h-4 w-4 ${flagged ? 'text-red-600' : 'text-ink-faint'}`} />
					<h3 className="text-sm font-semibold">{title}</h3>
				</div>
				{count !== undefined && (
					<span
						className={`rounded-md px-2 py-0.5 text-xs font-medium tabular-nums ${
							flagged ? 'bg-red-100 text-red-800' : 'bg-slate-100 text-slate-700'
						}`}
					>
						{count}
						{countLabel ? ` ${countLabel}` : ''}
					</span>
				)}
			</div>
			<div className="flex-1">{children}</div>
			{footer && <div className="border-t border-line px-4 py-2.5 text-sm">{footer}</div>}
		</div>
	)
}

function Row({ to, href, primary, secondary, aside }: { to?: string; href?: string; primary: string; secondary?: string; aside?: React.ReactNode }) {
	const body = (
		<>
			<div className="min-w-0">
				<div className="truncate text-sm font-medium">{primary}</div>
				{secondary && <div className="truncate text-xs text-ink-soft">{secondary}</div>}
			</div>
			{aside && <div className="shrink-0 text-right text-sm tabular-nums">{aside}</div>}
		</>
	)
	const className = 'flex items-center justify-between gap-3 px-4 py-2.5'
	if (to) return <Link to={to} className={`${className} hover:bg-canvas`}>{body}</Link>
	if (href)
		return (
			<a href={href} target="_blank" rel="noreferrer" className={`${className} hover:bg-canvas`}>
				{body}
			</a>
		)
	return <div className={className}>{body}</div>
}

function FailedInstances({ rows }: { rows: Overview['attention']['failedInstances'] }) {
	return (
		<AttentionCard icon={ServerCrash} title="Failed instance setups" count={rows.length}>
			{rows.length ? (
				<div className="divide-y divide-line">
					{rows.map((r) => (
						<Row
							key={r.id}
							to={`/subscriptions/${r.id}`}
							primary={r.email ?? 'Unknown customer'}
							secondary={r.error ?? `${r.planSlug} · ${formatDate(r.createdAt)}`}
							aside={<span className="text-xs text-ink-soft">{r.planSlug}</span>}
						/>
					))}
				</div>
			) : (
				<EmptyState message="Every paid order has an instance." />
			)}
		</AttentionCard>
	)
}

function UpcomingDeletions({ rows }: { rows: Overview['attention']['upcomingDeletions'] }) {
	return (
		<AttentionCard icon={Trash2} title="Deletions in the next 7 days" count={rows.length}>
			{rows.length ? (
				<div className="divide-y divide-line">
					{rows.map((r) => (
						<Row
							key={r.id}
							to={`/subscriptions/${r.id}`}
							primary={r.email ?? 'Unknown customer'}
							secondary={r.planSlug}
							aside={<span className="text-xs text-ink-soft">{formatDate(r.deletionScheduledAt)}</span>}
						/>
					))}
				</div>
			) : (
				<EmptyState message="No instances are due for deletion this week." />
			)}
		</AttentionCard>
	)
}

function UnpaidCard() {
	const { data, isPending, error, refetch } = useQuery({
		queryKey: ['overview-payments'],
		queryFn: () => apiFetch<UnpaidInvoices>('/api/admin/overview/payments'),
	})
	return (
		<AttentionCard
			icon={CreditCard}
			title="Unpaid invoices"
			count={data ? `${data.count}${data.capped ? '+' : ''}` : undefined}
		>
			{isPending ? (
				<Spinner />
			) : error ? (
				<ErrorState error={error} onRetry={refetch} />
			) : data.rows.length ? (
				<div className="divide-y divide-line">
					{data.rows.map((r) => (
						<Row
							key={r.id}
							href={r.chargebeeUrl ?? undefined}
							primary={r.email ?? r.customerId}
							secondary={`${r.status === 'not_paid' ? 'Gave up collecting' : 'In dunning'}${r.dueSince ? ` · due ${formatDate(r.dueSince)}` : ''}`}
							aside={money(r.amountDue, r.currency)}
						/>
					))}
				</div>
			) : (
				<EmptyState message="No customers owe money." />
			)}
		</AttentionCard>
	)
}

interface SupportThreads {
	rows: { id: string; subject: string; from: { name: string; email: string }; lastAt: string }[]
	nextCursor: string | null
}

function SupportCard() {
	// Both statuses mean the customer is waiting on us: 'open' is new, 'awaiting'
	// is a follow-up after we replied. Gmail search is the costly part, so this
	// is not refetched on every focus.
	const { data, isPending, error, refetch } = useQuery({
		queryKey: ['overview-support'],
		queryFn: async (): Promise<SupportThreads> => {
			const [open, awaiting] = await Promise.all(
				['open', 'awaiting'].map((s) => apiFetch<SupportThreads>(`/api/admin/support/threads?status=${s}`)),
			)
			return {
				rows: [...open.rows, ...awaiting.rows].sort((a, b) => b.lastAt.localeCompare(a.lastAt)),
				nextCursor: open.nextCursor ?? awaiting.nextCursor,
			}
		},
		staleTime: 5 * 60 * 1000,
	})
	return (
		<AttentionCard
			icon={Inbox}
			title="Unanswered support emails"
			count={data ? `${data.rows.length}${data.nextCursor ? '+' : ''}` : undefined}
			footer={
				<Link to="/support" className="font-medium text-ink-soft hover:text-ink">
					Open support inbox →
				</Link>
			}
		>
			{isPending ? (
				<Spinner />
			) : error ? (
				<ErrorState error={error} onRetry={refetch} />
			) : data.rows.length ? (
				<div className="divide-y divide-line">
					{data.rows.slice(0, 5).map((t) => (
						<Row
							key={t.id}
							to="/support"
							primary={t.subject || '(no subject)'}
							secondary={t.from.email}
							aside={<span className="text-xs text-ink-soft">{formatDate(t.lastAt)}</span>}
						/>
					))}
				</div>
			) : (
				<EmptyState message="Inbox zero — nothing waiting for a reply." />
			)}
		</AttentionCard>
	)
}

// --- Recent activity ---------------------------------------------------------

function ListCard({
	title,
	rows,
	empty,
	footer,
}: {
	title: string
	rows: { key: string; primary: string; secondary: string; to?: string; badge?: string }[]
	empty: string
	footer?: { label: string; to: string }
}) {
	return (
		<div className="card flex flex-col">
			<h3 className="border-b border-line px-4 py-3 text-sm font-semibold">{title}</h3>
			<div className="flex-1">
				{rows.length ? (
					<div className="divide-y divide-line">
						{rows.map((r) => (
							<Row
								key={r.key}
								to={r.to}
								primary={r.primary}
								secondary={r.secondary}
								aside={r.badge ? <StatusBadge value={r.badge} /> : undefined}
							/>
						))}
					</div>
				) : (
					<EmptyState message={empty} />
				)}
			</div>
			{footer && (
				<div className="border-t border-line px-4 py-2.5 text-sm">
					<Link to={footer.to} className="font-medium text-ink-soft hover:text-ink">
						{footer.label} →
					</Link>
				</div>
			)}
		</div>
	)
}

// --- Trends ------------------------------------------------------------------

// Categorical slots 1 and 2 of the dataviz reference palette (validated for
// CVD separation against a white surface).
const SERIES = [
	{ key: 'created', label: 'New subscriptions', color: '#2a78d6' },
	{ key: 'cancelled', label: 'Cancellations', color: '#eb6834' },
] as const

function monthLabel(month: string): string {
	const [y, m] = month.split('-').map(Number)
	return new Date(y, m - 1, 1).toLocaleString(undefined, { month: 'short' })
}

function TrendChart({ trend }: { trend: Overview['trend'] }) {
	const [hover, setHover] = useState<number | null>(null)
	const max = Math.max(1, ...trend.flatMap((t) => [t.created, t.cancelled]))
	// A round top for the axis so the gridlines land on whole numbers.
	const step = Math.max(1, Math.ceil(max / 4))
	const top = step * 4
	const ticks = [0, 1, 2, 3, 4].map((i) => i * step)
	const empty = trend.every((t) => t.created === 0 && t.cancelled === 0)

	// `relative` contains the sr-only table below: it is absolutely positioned,
	// and without a positioned ancestor it lands relative to the viewport at its
	// far-down static position — growing the document and scrolling the whole
	// shell instead of just <main>.
	return (
		<div className="card relative p-5">
			<div className="flex flex-wrap items-start justify-between gap-3">
				<div>
					<h3 className="text-sm font-semibold">New subscriptions vs cancellations</h3>
					<p className="text-xs text-ink-soft">Per month, last 6 months · from Chargebee billing events</p>
				</div>
				<ul className="flex gap-4 text-xs text-ink-soft">
					{SERIES.map((s) => (
						<li key={s.key} className="flex items-center gap-1.5">
							<span className="h-2.5 w-2.5 rounded-sm" style={{ background: s.color }} />
							{s.label}
						</li>
					))}
				</ul>
			</div>

			<div className="relative mt-5 h-56">
				{/* Gridlines + y labels */}
				<div className="absolute inset-0 bottom-6 left-8">
					{ticks.map((t) => (
						<div
							key={t}
							className="absolute left-0 right-0 border-t border-line"
							style={{ bottom: `${(t / top) * 100}%` }}
						>
							<span className="absolute -left-8 -translate-y-1/2 text-[11px] tabular-nums text-ink-faint">{t}</span>
						</div>
					))}
				</div>

				{/* Bars */}
				<div className="absolute inset-0 left-8 flex items-stretch">
					{trend.map((t, i) => (
						// A button per month so the tooltip opens on keyboard focus
						// as well as hover. The whole column is the hit target.
						<button
							type="button"
							key={t.month}
							aria-label={`${monthLabel(t.month)} ${t.month.slice(0, 4)}: ${t.created} new, ${t.cancelled} cancelled`}
							className="relative flex flex-1 cursor-default flex-col outline-none"
							onMouseEnter={() => setHover(i)}
							onMouseLeave={() => setHover(null)}
							onFocus={() => setHover(i)}
							onBlur={() => setHover(null)}
						>
							<div className={`flex w-full flex-1 items-end justify-center gap-0.5 rounded-t ${hover === i ? 'bg-canvas' : ''}`}>
								{SERIES.map((s) => (
									<div
										key={s.key}
										className="w-3 rounded-t sm:w-4"
										style={{ height: `${(t[s.key] / top) * 100}%`, background: s.color }}
									/>
								))}
							</div>
							<div className="h-6 w-full pt-1.5 text-center text-[11px] text-ink-soft">{monthLabel(t.month)}</div>

							{hover === i && (
								<div className="pointer-events-none absolute left-1/2 top-0 z-10 -translate-x-1/2 whitespace-nowrap rounded-lg border border-line bg-surface px-3 py-2 text-left text-xs shadow-sm">
									<div className="mb-1 font-semibold">{monthLabel(t.month)} {t.month.slice(0, 4)}</div>
									{SERIES.map((s) => (
										<div key={s.key} className="flex items-center gap-1.5">
											<span className="h-2 w-2 rounded-sm" style={{ background: s.color }} />
											<span className="text-ink-soft">{s.label}</span>
											<span className="ml-auto pl-3 font-medium tabular-nums">{t[s.key]}</span>
										</div>
									))}
								</div>
							)}
						</button>
					))}
				</div>

				{empty && (
					<div className="absolute inset-0 bottom-6 left-8 flex items-center justify-center text-sm text-ink-faint">
						<span className="bg-surface px-2">No subscription events recorded in the last 6 months.</span>
					</div>
				)}
			</div>

			{/* Same numbers as text, for screen readers. */}
			<table className="sr-only">
				<caption>New subscriptions and cancellations per month</caption>
				<thead>
					<tr>
						<th>Month</th>
						<th>New subscriptions</th>
						<th>Cancellations</th>
					</tr>
				</thead>
				<tbody>
					{trend.map((t) => (
						<tr key={t.month}>
							<td>{t.month}</td>
							<td>{t.created}</td>
							<td>{t.cancelled}</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	)
}

interface JourneySummary {
	trackingSince: string | null
	totals: { checkout: number; paid: number }
}

interface CouponDetail {
	coupon: { redemptions?: number }
}

function CheckoutCard() {
	// Both read external services (Rybbit, Chargebee) and are independent, so
	// either can fail without hiding the other.
	const journey = useQuery({
		queryKey: ['journey-summary', 30],
		queryFn: () => apiFetch<JourneySummary>('/api/admin/analytics/summary?days=30'),
		staleTime: 5 * 60 * 1000,
	})
	const comeback = useQuery({
		queryKey: ['coupon', 'COMEBACK20'],
		queryFn: () => apiFetch<CouponDetail>('/api/admin/coupons/COMEBACK20'),
		staleTime: 5 * 60 * 1000,
	})

	const abandoned = journey.data ? Math.max(0, journey.data.totals.checkout - journey.data.totals.paid) : null

	return (
		<AttentionCard
			icon={ShoppingCart}
			title="Abandoned checkouts"
			footer={
				<Link to="/customer-journey" className="font-medium text-ink-soft hover:text-ink">
					Customer journey →
				</Link>
			}
		>
			<div className="divide-y divide-line">
				<div className="px-4 py-3">
					<p className="text-xs font-medium text-ink-soft">Started checkout but didn't pay · last 30 days</p>
					{journey.isPending ? (
						<Spinner />
					) : journey.error ? (
						<ErrorState error={journey.error} onRetry={journey.refetch} />
					) : (
						<>
							<p className="mt-1 font-display text-3xl font-semibold tabular-nums">{num(abandoned ?? 0)}</p>
							<p className="text-xs text-ink-soft">
								{num(journey.data.totals.checkout)} started · {num(journey.data.totals.paid)} paid
							</p>
						</>
					)}
				</div>
				<div className="px-4 py-3">
					<p className="flex items-center gap-1 text-xs font-medium text-ink-soft">
						Came back with COMEBACK20
						<Link to="/coupons" className="text-ink-faint hover:text-ink" aria-label="Open coupons">
							<ExternalLink className="h-3 w-3" />
						</Link>
					</p>
					{comeback.isPending ? (
						<Spinner />
					) : comeback.error ? (
						<ErrorState error={comeback.error} onRetry={comeback.refetch} />
					) : (
						<>
							<p className="mt-1 font-display text-3xl font-semibold tabular-nums">
								{num(comeback.data.coupon.redemptions ?? 0)}
							</p>
							<p className="text-xs text-ink-soft">times redeemed, all time</p>
						</>
					)}
				</div>
			</div>
		</AttentionCard>
	)
}
